import type { DataGridFilterValue } from '@ibsys/kit';

/**
 * Значение отбора, задающее не условие по значению, а признак заполненности: остаются строки,
 * у которых значение есть. Грид держит этот признак оператором отбора (`operator: 'notEmpty'`),
 * а состояние таблицы — значением: оно и сохраняется вместе с видом таблицы, и уходит в запрос.
 */
export type NotEmptyFilterValue = { notEmpty: true };

/** Включённый признак заполненности — единственное значение, которым он задаётся */
export const NOT_EMPTY_FILTER_VALUE: NotEmptyFilterValue = { notEmpty: true };

/** Отбор задан признаком заполненности, а не значением */
export const isNotEmptyFilterValue = (value: unknown): value is NotEmptyFilterValue =>
  typeof value === 'object' && value !== null && (value as NotEmptyFilterValue).notEmpty === true;

/** Значение отбора из состояния таблицы — в конверт грида: признак заполненности едет оператором */
export const toDataGridFilterValue = (value: unknown): DataGridFilterValue | undefined => {
  if (isNotEmptyFilterValue(value)) return { operator: 'notEmpty' };
  return value === undefined ? undefined : { value };
};

/**
 * Конверт грида — в значение отбора для состояния таблицы.
 *
 * Пару в персистентном формате имеет только `notEmpty`. Оператор, которому пары нет, нельзя
 * просто пропустить, оставив `value`: отбор, восстановленный из настроек, стал бы условием на
 * равенство — молча и без ошибки типов. Поэтому такой отбор отбрасывается целиком и с записью в
 * консоль, а `never`-проверка роняет `yarn tsc`, как только кит объявит новый оператор: пару ему
 * заводят здесь же, а не обнаруживают по поведению.
 */
export const fromDataGridFilterValue = (filter: DataGridFilterValue | undefined): unknown => {
  if (filter?.operator === undefined) return filter?.value;
  if (filter.operator === 'notEmpty') return NOT_EMPTY_FILTER_VALUE;

  const unsupportedOperator: never = filter.operator;
  console.warn(`Отбор с оператором «${String(unsupportedOperator)}» не переносится в настройки таблицы и отброшен`);
  return undefined;
};
